import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { EditableColumnDef, EditableTable } from "../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { NonMandatoryCriterionDetails } from "../../../sc-models/scope-certificate-details-dto";
import { OptionType } from "../../../../../../shared/components/form-builder/form-config.model";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { useParams } from "react-router-dom";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";

interface NonMandatoryCriterionDetailsFormProps {
  nonMandatoryCriterionData: NonMandatoryCriterionDetails[];
  setNonMandatoryCriterionData: (data: NonMandatoryCriterionDetails[]) => void;
}

const validationSchema = Yup.object().shape({
  scNonMandatoryCriterionStandard: Yup.string().required("SC Non-Mandatory Criterion Standard is required"),
  scNonMandatoryCriterionStandardVersion: Yup.string().required("SC Non-Mandatory Criterion Standard Version is required"),
  scNonMandatoryCriterionNo: Yup.string().required("SC Non-Mandatory Criterion No. is required"),
  scNonMandatoryCriterionStatus: Yup.string().required("SC Non-Mandatory Criterion Status is required"),
});

const NonMandatoryCriterionDetailsForm: React.FC<NonMandatoryCriterionDetailsFormProps> = ({
  nonMandatoryCriterionData,
  setNonMandatoryCriterionData,
}) => {
  const { formtype } = useParams();
  const hasSubmitSC: boolean = formtype === 'submited-sc';
  const [data, setData] = useState<NonMandatoryCriterionDetails[]>(nonMandatoryCriterionData);
  const [tableData, setTableData] = useState<NonMandatoryCriterionDetails[]>(nonMandatoryCriterionData);
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const { notify } = useNotification();
  const { setRestrictPopUpSave, isRestrictPopUpSave } = useTransactionCertificateStore();

  const scNonMandatoryCriterionStandardValues: OptionType[] = [
    { label: "Organic Content Standard (OCS)", value: "Organic Content Standard (OCS)", code: "OCS" },
    { label: "Global Recycled Standard (GRS)", value: "Global Recycled Standard (GRS)", code: "GRS" },
    { label: "Recycled Claim Standard (RCS)", value: "Recycled Claim Standard (RCS)", code: "RCS" },
    { label: "Responsible Down Standard (RDS)", value: "Responsible Down Standard (RDS)", code: "RDS" },
    { label: "Responsible Wool Standard (RWS)", value: "Responsible Wool Standard (RWS)", code: "RWS" },
    { label: "Responsible Mohair Standard (RMS)", value: "Responsible Mohair Standard (RMS)", code: "RMS" },
    { label: "Responsible Alpaca Standard (RAS)", value: "Responsible Alpaca Standard (RAS)", code: "RAS" },
    { label: "Content Claim Standard (CCS)", value: "Content Claim Standard (CCS)", code: "CCS" },
  ];

  const scNonMandatoryCriterionStatusValues: OptionType[] = [
    { label: "Active", value: "Active", code: "A" },
    { label: "Inactive", value: "Inactive", code: "I" },
    { label: "Pending", value: "Pending", code: "P" },
    { label: "Suspended", value: "Suspended", code: "S" },
    { label: "Withdrawn", value: "Withdrawn", code: "W" },
  ];

  const formik = useFormik<NonMandatoryCriterionDetails>({
    initialValues: {
      scNonMandatoryCriterionStandard: "",
      scNonMandatoryCriterionStandardDescription: "",
      scNonMandatoryCriterionStandardVersion: "",
      scNonMandatoryCriterionNo: "",
      scNonMandatoryCriterionStatus: "",
      scNonMandatoryCriterionStatusDescription: "",
    },
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true, // This ensures formik reinitializes when data changes
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {

    if(columnId === 'scNonMandatoryCriterionStatus') {
      let optionCode = scNonMandatoryCriterionStatusValues.find(val => val.value === value)?.code || ""
      formik.setFieldValue('scNonMandatoryCriterionStatus', value);
      formik.setFieldValue('scNonMandatoryCriterionStatusDescription', value);
      // Trigger validation after setting the value
      formik.validateField('scNonMandatoryCriterionStatus');
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex ? { ...row, ['scNonMandatoryCriterionStatus']: value, 'scNonMandatoryCriterionStatusDescription': value } : row
        )
      );
    } else if(columnId === 'scNonMandatoryCriterionStandard') {
      let optionCode = scNonMandatoryCriterionStandardValues.find(val => val.value === value)?.code || ""
      formik.setFieldValue('scNonMandatoryCriterionStandard', value);
      formik.setFieldValue('scNonMandatoryCriterionStandardDescription', value);
      // Trigger validation after setting the value
      formik.validateField('scNonMandatoryCriterionStandard');
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex ? { ...row, ['scNonMandatoryCriterionStandard']: value, 'scNonMandatoryCriterionStandardDescription': value } : row
        )
      );
    } else {
      formik.setFieldValue(columnId, value);
      // Trigger validation after setting the value
      formik.validateField(columnId);
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row
        )
      );
    }
  };

  const handleAddNewRow = () => {
    const newRow: NonMandatoryCriterionDetails = {
      scNonMandatoryCriterionStandard: "",
      scNonMandatoryCriterionStandardDescription: "",
      scNonMandatoryCriterionStandardVersion: "",
      scNonMandatoryCriterionNo: "",
      scNonMandatoryCriterionStatus: "",
      scNonMandatoryCriterionStatusDescription: "",
    };
    
    setRestrictPopUpSave(true);
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    
    // Ensure formik is properly initialized
    formik.setValues(newRow);
    formik.setTouched({});
    formik.setErrors({});
  };

  const handleCancelEdit = async () => {
    setRestrictPopUpSave(false);
    setData(tableData);
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const handleSaveRow = () => {
    // Check if required fields are filled
    const { scNonMandatoryCriterionStandard, scNonMandatoryCriterionStandardVersion, scNonMandatoryCriterionNo, scNonMandatoryCriterionStatus } = formik.values;
    
    const missingFields = [];
    if (!scNonMandatoryCriterionStandard) missingFields.push('SC Non-Mandatory Criterion Standard');
    if (!scNonMandatoryCriterionStandardVersion) missingFields.push('SC Non-Mandatory Criterion Standard Version');
    if (!scNonMandatoryCriterionNo) missingFields.push('SC Non-Mandatory Criterion No.');
    if (!scNonMandatoryCriterionStatus) missingFields.push('SC Non-Mandatory Criterion Status');
    
    if (missingFields.length > 0) {
      notify(`Please fill in: ${missingFields.join(', ')}`, 'error');
      return;
    }

    if (editingRowIndex === null) {
      notify('No row is being edited', 'error');
      return;
    }

    const updatedRow = formik.values;
    const newData = data.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      );
    
    setData(newData);
    setTableData(newData);
    setRestrictPopUpSave(false);
    
    // Call the parent function to update the data
    setNonMandatoryCriterionData(newData);
    
    setEditingRowIndex(-1);
    formik.resetForm();
    
    notify('Row saved successfully', 'success');
  };

  useEffect(() => {
    setData(nonMandatoryCriterionData);
    setTableData(nonMandatoryCriterionData);
    
    return (()=>{
      setRestrictPopUpSave(false);
    })
  }, [nonMandatoryCriterionData]);



  const columns: EditableColumnDef<NonMandatoryCriterionDetails>[] = [
    {
      accessorKey: "scNonMandatoryCriterionStandard",
      header: "SC Non-Mandatory Criterion Standard",
      Header: <span> <i style={{ color: 'red' }}>* </i>SC Non-Mandatory Criterion Standard</span>,
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          name="scNonMandatoryCriterionStandard"
          value={value}
          placeholder="Select Non-Mandatory Criterion Standard"
          options={scNonMandatoryCriterionStandardValues}
          onChange={(e) => {
            onChange(e.target.value)
          }}
        />
      ),
    },
    {
      accessorKey: "scNonMandatoryCriterionStandardVersion",
      header: "SC Non-Mandatory Criterion Standard Version",
      Header: <span> <i style={{ color: 'red' }}>* </i>SC Non-Mandatory Criterion Standard Version</span>,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="scNonMandatoryCriterionStandardVersion"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Non-Mandatory Criterion Standard Version"
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      accessorKey: "scNonMandatoryCriterionNo",
      header: "SC Non-Mandatory Criterion No.",
      Header: <span> <i style={{ color: 'red' }}>* </i>SC Non-Mandatory Criterion No.</span>,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="scNonMandatoryCriterionNo"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Non-Mandatory Criterion No."
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      accessorKey: "scNonMandatoryCriterionStatus",
      header: "SC Non-Mandatory Criterion Status",
      Header: <span> <i style={{ color: 'red' }}>* </i>SC Non-Mandatory Criterion Status</span>,
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          name="scNonMandatoryCriterionStatus"
          value={value}
          placeholder="Select Non-Mandatory Criterion Status"
          options={scNonMandatoryCriterionStatusValues}
          onChange={(e) => {
            onChange(e.target.value)
          }}
        />
      ),
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setRestrictPopUpSave(true);
    setEditingRowIndex(rowIndex);
    
    // Ensure all required fields are set in formik
    const rowData = data[rowIndex];
    const formikValues = {
      scNonMandatoryCriterionStandard: rowData.scNonMandatoryCriterionStandard || "",
      scNonMandatoryCriterionStandardDescription: rowData.scNonMandatoryCriterionStandardDescription || "",
      scNonMandatoryCriterionStandardVersion: rowData.scNonMandatoryCriterionStandardVersion || "",
      scNonMandatoryCriterionNo: rowData.scNonMandatoryCriterionNo || "",
      scNonMandatoryCriterionStatus: rowData.scNonMandatoryCriterionStatus || "",
      scNonMandatoryCriterionStatusDescription: rowData.scNonMandatoryCriterionStatusDescription || "",
    };
    
    formik.setValues(formikValues);
    
    // Reset touched state to allow validation
    formik.setTouched({});
    
    // Force formik to revalidate
    setTimeout(() => {
      formik.validateForm();
    }, 100);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    
    // Call the parent function to update the data
    setNonMandatoryCriterionData(updatedData);
  };

  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton onClick={handleAddNewRow} sx={{ mb: 2 }} disabled={!formik.isValid || hasSubmitSC || isRestrictPopUpSave}>
            Add Non-Mandatory Criterion Details
          </StyledButton>
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
          enableEditing={!hasSubmitSC}
        />
      </Box>
    </div>
  );
};

export default NonMandatoryCriterionDetailsForm; 